import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {CompanyDto} from "../../../models/admin_owner/company/company-dto";
import {ContractHttpService} from "../_services/contract-http.service";
import {CompanyEdto} from "../../company/x-models/company-edto";
import {
  ContractBase,
  contractCoversDay,
  contractEndedBefore,
  contractStartsAfter
} from "../../company/x-models/contract-base";
import {Tuple} from "../../../models/_generic/tuple";
import {CustomerValues} from "../../company/x-models/customer-values";
import {ContractCreateModel} from "../_models/contract-create-model";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {ContractInformationComponent} from "../_components/contract-information/contract-information.component";
import {NavbarService} from "../../../x-components/navbar-base/navbar.service";
import {
  companyToday,
  DayKey,
  DEFAULT_COMPANY_TIME_ZONE,
  isDayKey,
  startOfMonth
} from "../../../_shared/time/company-day";
import {DayKeyPipe} from "../../../_shared/time/day-key.pipe";

/**
 * Centered-modal contract workspace for a single company. Opened from the owner companies
 * table; shows the active contract (with usage meters) plus collapsible upcoming/past
 * sections, and an inline create/edit form. Reuses ContractHttpService + the contract-information row.
 */
@Component({
  selector: 'app-company-contracts',
  standalone: true,
  imports: [CommonModule, FormsModule, ContractInformationComponent, DayKeyPipe],
  templateUrl: './company-contracts.component.html'
})
export class CompanyContractsComponent implements OnChanges {
  @Input() company!: CompanyDto;
  @Output() closed = new EventEmitter<void>();

  constructor(private contractHttpService: ContractHttpService, private gas: GlobalAlertService,
              private navbar: NavbarService) {
  }

  /**
   * Today, for sorting the company's contracts into running, upcoming and ended (#802). This dialog inspects any
   * company and does not know its zone, so it uses the platform default; every customer is in Berlin today. The
   * server decides with the company's own zone wherever it matters (device cap, events).
   */
  today: DayKey = companyToday(DEFAULT_COMPANY_TIME_ZONE);

  /** Pixel offset of the navbar bottom edge (56px collapsed, 144px when the sub-navbar is expanded). */
  get navbarOffset(): number {
    return this.navbar.hasExpanded() ? 144 : 56;
  }

  company_details?: CompanyEdto;
  company_values?: Tuple<CustomerValues, CustomerValues>;

  current?: ContractBase;
  upcoming: ContractBase[] = [];
  past: ContractBase[] = [];

  /** Two-page layout: the active contract, or the upcoming/past history list. */
  page: 'current' | 'history' = 'current';

  editMode = false;
  form = new ContractForm();

  ngOnChanges(): void {
    this.editMode = false;
    this.page = 'current';
    this.form.reset();
    this.company_details = undefined;
    this.company_values = undefined;
    if (this.company) this.load();
  }

  private load(): void {
    this.contractHttpService.getCompany(this.company.id)
      .withLoadingIndicator()
      .subscribe({
        next: details => {
          this.company_details = details;
          this.recompute();
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Laden der Unternehmensdetails", AlertLevel.error);
          this.company_details = undefined;
        }
      });

    this.contractHttpService.getCustomerValues(this.company.id)
      .withLoadingIndicator()
      .subscribe({
        next: values => this.company_values = values,
        error: err => {
          console.error(err);
          this.company_values = undefined;
        }
      });
  }

  private recompute(): void {
    const contracts = this.company_details?.contracts ?? [];
    this.current = contracts.find(x => contractCoversDay(x, this.today));
    this.upcoming = contracts.filter(x => contractStartsAfter(x, this.today));
    this.past = contracts.filter(x => contractEndedBefore(x, this.today));
  }

  /** Width % for a usage meter (clamped to 100). */
  meterPct(used: number | undefined, limit: number | undefined): number {
    if (!limit || limit <= 0) return 0;
    return Math.min(100, Math.round(((used ?? 0) / limit) * 100));
  }

  /** True when usage exceeds the contracted limit (renders the meter red). */
  meterOver(used: number | undefined, limit: number | undefined): boolean {
    return (used ?? 0) > (limit ?? 0);
  }

  createNew(): void {
    this.form.reset();
    this.editMode = true;
  }

  editContract(contract: ContractBase): void {
    this.form.load(contract);
    this.editMode = true;
  }

  cancelEdit(): void {
    this.editMode = false;
    this.form.reset();
  }

  save(): void {
    (this.form.id
        ? this.contractHttpService.update(this.company.id, this.form.id, this.form.toModel())
        : this.contractHttpService.create(this.company.id, this.form.toModel())
    ).withLoadingIndicator().subscribe({
      next: details => {
        this.company_details = details;
        this.recompute();
        this.gas.createAndShow("Erfolg", "Vertrag erfolgreich gespeichert", AlertLevel.success);
        this.editMode = false;
        this.form.reset();
      },
      error: err => {
        console.error(err);
        this.gas.createAndShow("Fehler", "Fehler beim Speichern des Vertrags", AlertLevel.error);
      }
    });
  }

  delete(): void {
    if (!this.form.id) return;
    if (!confirm("Sind Sie sicher, dass Sie den Vertrag löschen möchten?")) return;

    this.contractHttpService.delete(this.company.id, this.form.id)
      .withLoadingIndicator()
      .subscribe({
        next: details => {
          this.company_details = details;
          this.recompute();
          this.gas.createAndShow("Erfolg", "Vertrag erfolgreich gelöscht", AlertLevel.success);
          this.editMode = false;
          this.form.reset();
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Löschen des Vertrags", AlertLevel.error);
        }
      });
  }

  close(): void {
    this.closed.emit();
  }
}

/**
 * Editable view-model for a contract (mirrors the legacy contract-manager helper).
 *
 * The two days are plain `yyyy-MM-dd` strings, bound straight to the date inputs and posted as they are (#802).
 * The end is the contract's last day, included. They used to be `InputDate`s, which posted midnight in the
 * admin's browser zone as an instant.
 */
class ContractForm {
  id: string | undefined;
  start: DayKey = '';
  end: DayKey = '';
  shard_count: number = 1;
  device_count: number = 3;
  device_minimum_months: number = 2;
  /** Parallel virtual events (#664). `-1` = unbegrenzt, `0` = keine — the deliberate default for a new one. */
  virtual_event_count: number = 0;

  /** A new contract runs from the 1st of the current month to 31.12. of the same year. */
  reset_dates(): void {
    this.start = startOfMonth(companyToday(DEFAULT_COMPANY_TIME_ZONE));
    this.end = `${this.start.slice(0, 4)}-12-31`;
  }

  reset(): void {
    this.reset_dates();
    this.id = undefined;
    this.shard_count = 1;
    this.device_count = 3;
    this.device_minimum_months = 2;
    this.virtual_event_count = 0;
  }

  load(contract: ContractBase): void {
    this.id = contract.id;
    // Anything that is not a day (an instant from a server built before #802) leaves the field empty rather
    // than guessing a day in the browser's zone.
    this.start = isDayKey(contract.validFrom) ? contract.validFrom : '';
    this.end = isDayKey(contract.validTo) ? contract.validTo : '';
    this.shard_count = contract.shardCount;
    this.device_count = contract.deviceCount;
    this.device_minimum_months = contract.minimalMonthsPerDevice;
    this.virtual_event_count = contract.virtualEventCount;
  }

  toModel(): ContractCreateModel {
    return new ContractCreateModel(this.start, this.end, this.shard_count, this.device_count, this.device_minimum_months, this.virtual_event_count);
  }
}
